import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, MapPin, Phone, ShieldCheck } from "@phosphor-icons/react";
import { serviceImage } from "../../lib/api";
import { useContent, pick } from "../../lib/content";
import { useSite } from "../../lib/site";
import { useServices } from "../../lib/services";
import { findLocation, COMBO_SERVICE_SLUGS } from "../../lib/locations";
import SEO from "../../components/SEO";
import CTABand from "../../components/public/CTABand";

export default function ServiceLocation() {
  const { slug, location } = useParams();
  const c = useContent();
  const site = useSite();
  const services = useServices();
  const svc = services.find((s) => s.slug === slug);
  const loc = findLocation(location);

  if (!loc || !COMBO_SERVICE_SLUGS.includes(slug)) return <Navigate to={`/services/${slug}`} replace />;
  if (!svc) return <Navigate to="/services" replace />;

  const businessName = site?.business_name || "ATC Home Pro's";
  const title = `${svc.title} in ${loc.full} — ${businessName}`;
  const description = `Looking for ${svc.title.toLowerCase()} in ${loc.full}? ${businessName} has been delivering ${svc.title.toLowerCase()} across ${loc.region} since ${site?.founding_year || 1991}. Lifetime labor warranty, in-house crew, free estimates.`;

  const baseUrl = (site?.site_url || "").replace(/\/$/, "");
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": `${svc.title} in ${loc.full}`,
      "description": description,
      "serviceType": svc.title,
      "provider": { "@type": "GeneralContractor", "name": businessName, "url": baseUrl, "telephone": site?.phone },
      "areaServed": { "@type": "Place", "name": loc.full },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${baseUrl}/` },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
        { "@type": "ListItem", "position": 3, "name": svc.title, "item": `${baseUrl}/services/${svc.slug}` },
        { "@type": "ListItem", "position": 4, "name": loc.full, "item": `${baseUrl}/services/${svc.slug}/${loc.slug}` },
      ],
    },
  ];

  return (
    <div data-testid={`service-location-${svc.slug}-${loc.slug}`}>
      <SEO path={`/services/${svc.slug}/${loc.slug}`} title={title} description={description} image={svc.image_path} jsonLd={jsonLd[0]} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd[1]) }} />

      {/* HERO */}
      <section className="relative bg-atc-black text-white pt-20 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={serviceImage(svc)} alt={`${svc.title} ${loc.full}`} className="w-full h-full object-cover opacity-50 grayscale" />
          <div className="absolute inset-0 bg-gradient-to-r from-atc-black via-atc-black/80 to-transparent" />
        </div>
        <div className="container-max relative">
          <Link to={`/services/${svc.slug}`} className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-mono uppercase tracking-widest mb-6" data-testid="back-to-service">
            <ArrowLeft size={14} weight="bold" /> {svc.title}
          </Link>
          <div className="overline mb-4 text-atc-red flex items-center gap-2"><MapPin size={14} weight="bold" /> {loc.full}</div>
          <h1 className="h-display">{svc.title}<br/><span className="text-atc-red">in {loc.name}.</span></h1>
          <p className="mt-6 text-white/80 text-lg max-w-2xl">{description}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" className="btn-primary" data-testid="combo-cta-primary">Get a Free Estimate <ArrowUpRight size={18} weight="bold" /></Link>
            {site?.phone && (
              <a href={`tel:${site.phone}`} className="btn-ghost-white" data-testid="combo-cta-call">
                <Phone size={16} weight="bold" /> {site.phone}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="section-pad bg-atc-paper">
        <div className="container-max grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline mb-4">Overview</div>
            <h2 className="h-section">Local craft.<br />Lifetime warranty.</h2>
            <div className="rule-red mt-6" />
          </div>
          <div className="md:col-span-7">
            <p className="text-lg leading-relaxed text-atc-charcoal">{svc.long_description || svc.short_description}</p>
            <p className="mt-5 text-lg leading-relaxed text-atc-charcoal">
              We&apos;ve delivered {svc.title.toLowerCase()} projects throughout {loc.region} — from quick fixes to multi-week builds. {loc.anchor}
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      {svc.features?.length > 0 && (
        <section className="section-pad bg-white border-y-2 border-atc-black">
          <div className="container-max">
            <div className="mb-12">
              <div className="overline mb-4">What&apos;s included</div>
              <h2 className="h-section">{svc.title} services for<br/>{loc.name} homeowners.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-atc-black border-2 border-atc-black">
              {svc.features.map((f, i) => (
                <div key={i} className="bg-white p-5 flex items-start gap-3">
                  <Check size={22} weight="bold" className="text-atc-red shrink-0 mt-1" />
                  <span className="text-atc-charcoal">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WARRANTY */}
      <section className="section-pad bg-atc-paper">
        <div className="container-max grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="overline mb-4">Our promise</div>
            <h2 className="h-section">Lifetime warranty on labor in {loc.name}.</h2>
          </div>
          <div className="md:col-span-6 border-2 border-atc-black p-8 bg-white">
            <ShieldCheck size={48} weight="bold" className="text-atc-red" />
            <p className="mt-5 text-lg leading-relaxed text-atc-charcoal">
              Every {svc.title.toLowerCase()} project we complete in {loc.full} is backed by a lifetime warranty on labor. If anything we installed fails because of workmanship, we come back and fix it free — for as long as you own the home.
            </p>
          </div>
        </div>
      </section>

      <CTABand phone={site?.phone} />
    </div>
  );
}
